import { Button } from '../../../../../../widgets/buttons';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { ColorPicker } from '../../../../../../widgets/colorPicker';
import { FormControl } from '../../../../../../widgets/formControl';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchQuery, useLazyLoadQuery, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import Select from 'react-select';
import Loader from 'src/components/widgets/loader';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { composerQueriesImagesCategoriesQuery } from '../../../configurator-composer/composer/__generated__/composerQueriesImagesCategoriesQuery.graphql';
import { composerQueriesImagesMacroQueriesCategories_fragment$key } from '../../../configurator-composer/composer/__generated__/composerQueriesImagesMacroQueriesCategories_fragment.graphql';
import { composerQueriesImagesMacroQueriesCategoriesFragmentQuery } from '../../../configurator-composer/composer/__generated__/composerQueriesImagesMacroQueriesCategoriesFragmentQuery.graphql';
import { composerQueriesImagesQueriesQuery } from '../../../configurator-composer/composer/__generated__/composerQueriesImagesQueriesQuery.graphql';
import {
    composerCategoriesQuery,
    composerCategorySelectionQuery,
    composerQueriesImagesMacroQueriesCategoriesFragment
} from '../../../configurator-composer/composer/composerQueries';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerTemplateNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { ComposerAreaNode, ComposerTemplateNode } from '../attributes/composerAttributesModels';

interface ComposerDesignEditorProps {
    template: ComposerTemplateNode;
    area: ComposerAreaNode;
    design: MPlaza.Design;
}

type EditorMode = 'image' | 'text';

type RestrictionState = {
    lockAspect: boolean;
    restrictBounds: boolean;
    snapToGrid: boolean;
};

const ComposerDesignToolbar: React.FC<{ activeMode: EditorMode | null; onModeChange: (mode: EditorMode) => void }> = ({ activeMode, onModeChange }) => (
    <ToolbarRow>
        <FormControl label='Image'>
            <Button type='button' data-active={activeMode === 'image'} onClick={() => onModeChange('image')}>
                Add new image
            </Button>
        </FormControl>
        <FormControl label='Text'>
            <Button type='button' data-active={activeMode === 'text'} onClick={() => onModeChange('text')}>
                Add new text
            </Button>
        </FormControl>
    </ToolbarRow>
);
type TextJustify = 'left' | 'center' | 'right' | 'justify';
interface ComposerDesignTextItemInfo {
    text: string;
    itemName: string;
    fontFamily: string;
    color: string;
    justify: TextJustify;
    isPath: boolean;
    restrictions: ComposerDesignTextItemInfoRestriction;
}

interface ComposerDesignImageItemInfo {
    id: number;
    url: string;
    name: string;
    restrictions: ComposerDesignImageItemInfoRestriction;
}

interface ComposerDesignTextItemInfoRestriction {
    textEditable: boolean;
    textMoveable: boolean;
    textRotable: boolean;
    textResizable: boolean;
    textDeletable: boolean;
    isAlwaysOnTop: boolean;
    isPrintable: boolean;
    printableInPreview: boolean;
    fontFamilyEditable: boolean;
    textColorEditable: boolean;
    fontWeightEditable: boolean;
    textPathEditable: boolean;
    uppercaseEditable: boolean;
    minCharacters: number;
    maxCharacters: number;
    paddingString: string;
    padType: 'start' | 'end' | 'both';
}

interface ComposerDesignImageItemInfoRestriction {
    imageReplaceable: boolean;
    imageMoveable: boolean;
    imageRotable: boolean;
    imageResizable: boolean;
    imageDeletable: boolean;
    alwaysOnTop: boolean;
    isPrintable: boolean;
    printableInPreview: boolean;
}

interface ComposerCategoriesImages {
    id: string;
    name: string;
    url: string;
}

type TextEditorTab = 'basic' | 'formatting' | 'restrictions';
const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 70%;
    height: 100%;
    gap: 12px;
    padding: 12px;
`;

const CanvasContainer = styled.div`
    flex: 1;
    canvas {
        display: block;
    }
`;

const ToolbarRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 12px;
`;

const FloatingPanel = styled.div`
    position: fixed;
    top: 0;
    right: 200px;
    z-index: 8;
    width: 320px;
    padding: 16px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
    border: 1px solid ${({ theme }) => theme.app.borderColor};
    user-select: none;
`;

const PanelHeader = styled.div<{ $dragging: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
    color: ${({ theme }) => theme.app.primary};
`;

const PanelTitle = styled.span`
    font-size: 16px;
    font-weight: 600;
`;

const CloseButton = styled.button`
    border: none;
    position: relative;
    background: transparent;
    color: ${({ theme }) => theme.app.secondary};
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    z-index: 1000;
    &:hover {
        color: ${({ theme }) => theme.app.primary};
        background: ${({ theme }) => theme.app.secondary};
    }
`;

const PanelBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 12px;
`;

const PanelSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const InlineInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.app.borderColor};
    color: ${({ theme }) => theme.app.primary};
    &:focus {
        outline: 2px solid ${({ theme }) => theme.app.primary};
    }
`;

const InlineActions = styled.div`
    display: flex;
    gap: 8px;
`;

const InlineFileInput = styled.input`
    width: 100%;
    padding: 12px;
    border-radius: 10px;
    border: 1px dashed ${({ theme }) => theme.app.borderColor};
    background: ${({ theme }) => theme.app.secondary};
    color: ${({ theme }) => theme.app.secondary};
    cursor: pointer;
`;

const RestrictionGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: ${({ theme }) => theme.app.secondary};
    }
    input[type='checkbox'] {
        accent-color: ${({ theme }) => theme.app.primary};
    }
`;

const RestrictionsScrollContainer = styled.div`
    max-height: 300px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.app.borderColor};
    border-radius: 8px;
    background: white;
`;

const RestrictionLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${({ theme }) => theme.app.secondary};
    cursor: pointer;
    padding: 4px 0;

    &:hover {
        color: ${({ theme }) => theme.app.primary};
    }
`;

const TabsContainer = styled.div`
    display: flex;
    gap: 4px;
    border-bottom: 1px solid ${({ theme }) => theme.app.borderColor};
    margin-bottom: 12px;
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 8px 16px;
    border: none;
    background: ${({ $active, theme }) => ($active ? theme.app.primary : 'transparent')};
    color: ${({ $active, theme }) => ($active ? 'white' : theme.app.secondary)};
    cursor: pointer;
    border-radius: 8px 8px 0 0;
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? '600' : '400')};
    transition: all 0.2s;

    &:hover {
        background: ${({ $active, theme }) => ($active ? theme.app.primary : theme.app.secondary)};
        color: ${({ $active, theme }) => ($active ? 'white' : theme.app.primary)};
    }
`;

const TabContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 8px;
`;

const ImageGridItem = styled.button`
    border: 2px solid ${({ theme }) => theme.app.primary};
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
`;

const getDefaultTextItemInfo = (): ComposerDesignTextItemInfo => ({
    text: '',
    itemName: '',
    fontFamily: 'Arial',
    color: '#000000',
    justify: 'left',
    isPath: true,
    restrictions: getDefaultTextRestrictions()
});

const getDefaultTextRestrictions = (): ComposerDesignTextItemInfoRestriction => ({
    textEditable: true,
    textMoveable: true,
    textRotable: true,
    textResizable: true,
    textDeletable: true,
    isAlwaysOnTop: false,
    isPrintable: true,
    printableInPreview: true,
    fontFamilyEditable: true,
    textColorEditable: true,
    fontWeightEditable: true,
    textPathEditable: true,
    uppercaseEditable: false,
    minCharacters: 0,
    maxCharacters: 0,
    paddingString: '',
    padType: 'start'
});

const getDefaultImageItemInfo = (): ComposerDesignImageItemInfo => ({
    id: -1,
    url: '',
    name: '',
    restrictions: getDefaultImageRestrictions()
});

const getDefaultImageRestrictions = (): ComposerDesignImageItemInfoRestriction => ({
    imageReplaceable: true,
    imageMoveable: true,
    imageRotable: true,
    imageResizable: true,
    imageDeletable: true,
    alwaysOnTop: false,
    isPrintable: true,
    printableInPreview: true
});

export const ComposerDesignEditor: React.FC<ComposerDesignEditorProps> = ({ template, area, design }) => {
    const Environment = useRelayEnvironment();
    const containerRef = useRef<HTMLElement>(document.body);
    const canvasContainerRef = useRef<HTMLDivElement | null>(null);
    const customizerRef = useRef<MPlaza.Customizer | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const sideRef = useRef<MPlaza.Side>(ComposerTemplateNodeAdapter.fromAreaNodeToSide(area));
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
    const [panelPosition, setPanelPosition] = useState({ x: 24, y: 24 });
    const [dragPointerId, setDragPointerId] = useState<number | null>(null);
    const [selectedDesignItem, setSelectedDesignItem] = useState<MPlaza.DesignItem | null>(null);
    const selectedPaperItem: paper.Item = selectedDesignItem ? (customizerRef.current as any)?.findItemForDesignItem(selectedDesignItem) : null;
    const [isDragging, setIsDragging] = useState(false);
    const [itemInfo, setItemInfo] = useState<ComposerDesignTextItemInfo>(getDefaultTextItemInfo());
    const [imageItemInfo, setImageItemInfo] = useState<ComposerDesignImageItemInfo>(getDefaultImageItemInfo());
    const [activeTextTab, setActiveTextTab] = useState<TextEditorTab>('basic');
    const [restrictions, setRestrictions] = useState<RestrictionState>({
        lockAspect: true,
        restrictBounds: false,
        snapToGrid: false
    });
    const [selectedImageCategory, setSelectedImageCategory] = useState<string | null>(null);
    const [selectedMacroCategory, setSelectedMacroCategory] = useState<string | null>(null);
    const [currentImages, setCurrentImages] = useState<ComposerCategoriesImages[]>([]);
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((x) => [x.zkProduct, x.zkScene, x.sceneEditor]);
    const [saveDesign, selectedArea, selectedTemplate, fonts] = useComposerStore((state) => [
        state.updateDesign,
        state.selectedAreas,
        state.selectedTemplates,
        state.fonts
    ]);
    const model = useMemo(() => zkProduct, []);
    const [isReady, setIsReady] = useState(false);

    const imageCategoriesQuery = useLazyLoadQuery<composerQueriesImagesCategoriesQuery>(
        composerCategoriesQuery,
        { isLibrary: false, onlybackground: false },
        { fetchPolicy: 'store-and-network' }
    );

    const [imagesMacro, refetch] = useRefetchableFragment<
        composerQueriesImagesMacroQueriesCategoriesFragmentQuery,
        composerQueriesImagesMacroQueriesCategories_fragment$key
    >(composerQueriesImagesMacroQueriesCategoriesFragment, imageCategoriesQuery);

    const saveDesignChanges = () => {
        saveDesign(template.templateID, area.sideID, design);
    };

    const handleItemCreated = useCallback((i: paper.Item) => {
        setSelectedDesignItem((customizerRef.current as any)?.findDesignItemByGuid(i.data?.guid));
    }, []);

    const handleSelectionChanged = useCallback((data: { designItem: MPlaza.DesignItem; selected: any }) => {
        if (!data?.selected) {
            setSelectedDesignItem(null);
            setItemInfo(getDefaultTextItemInfo());
            setEditorMode(null);
            return;
        }
        const currentDesignItem = data.designItem;
        setSelectedDesignItem(data.selected ? currentDesignItem : null);
        const currentPaperItem = (customizerRef.current as any)?.findItemForDesignItem(currentDesignItem);
        const textConstraints = currentDesignItem.get('constraints') || new MPlaza.DesignItemConstraints();
        const isImage = currentPaperItem instanceof paper.Raster;
        if (isImage) {
            setItemInfo(getDefaultTextItemInfo());
            setImageItemInfo(
                data.selected
                    ? {
                          id: currentDesignItem.get('imageID'),
                          url: currentPaperItem?.data?.imageUrl || '',
                          name: data.designItem?.get('name') || '',
                          restrictions: _.extend(getDefaultImageRestrictions(), {
                              imageReplaceable: textConstraints.get('canEdit'),
                              imageMoveable: textConstraints.get('canMove'),
                              imageRotable: textConstraints.get('canRotate'),
                              imageResizable: textConstraints.get('canResize'),
                              imageDeletable: textConstraints.get('canDelete'),
                              alwaysOnTop: textConstraints.get('isAlwaysOnTop'),
                              isPrintable: textConstraints.get('isPrintable'),
                              printableInPreview: !textConstraints.get('isPrintable') ? true : (textConstraints as any).get('keepInPreview')
                          })
                      }
                    : getDefaultImageItemInfo()
            );
            setEditorMode(data.selected ? 'image' : null);
            return;
        }

        setItemInfo(
            data.selected
                ? {
                      text: currentPaperItem?.data?.text || '',
                      itemName: data.designItem?.get('name') || '',
                      fontFamily: currentPaperItem?.fontFamily || '',
                      color: currentPaperItem?.data?.originalFillColor || '',
                      justify: currentPaperItem?.data?.justification || 'left',
                      isPath: currentPaperItem?.data?.isTextArea || false,
                      restrictions: _.extend(getDefaultTextRestrictions(), {
                          textEditable: textConstraints.get('canEdit'),
                          textMoveable: textConstraints.get('canMove'),
                          textRotable: textConstraints.get('canRotate'),
                          textResizable: textConstraints.get('canResize'),
                          textDeletable: textConstraints.get('canDelete'),
                          isAlwaysOnTop: textConstraints.get('isAlwaysOnTop'),
                          isPrintable: textConstraints.get('isPrintable'),
                          printableInPreview: !textConstraints.get('isPrintable') ? true : (textConstraints as any).get('keepInPreview'),
                          fontFamilyEditable: textConstraints.get('canChangeFontFamily'),
                          textColorEditable: textConstraints.get('canChangeFontColor'),
                          fontWeightEditable: textConstraints.get('canChangeFontWeight'),
                          textPathEditable: textConstraints.get('canChangeTextPathMode'),
                          uppercaseEditable: (textConstraints as any).get('toUppercase'),
                          minCharacters: textConstraints.get('minNrChars'),
                          maxCharacters: textConstraints.get('maxNrChars'),
                          paddingString: textConstraints.get('paddingString') || '',
                          padType:
                              textConstraints.get('paddingTypeID') === MPlaza.PaddingType.Start
                                  ? 'start'
                                  : textConstraints.get('paddingTypeID') === MPlaza.PaddingType.End
                                    ? 'end'
                                    : 'both'
                      })
                  }
                : getDefaultTextItemInfo()
        );
        setEditorMode(data.selected ? 'text' : null);
    }, []);

    useEffect(() => {
        (async () => {
            if (canvasContainerRef.current && containerRef.current && model && !isReady) {
                const customizer = new MPlaza.Customizer(false, false, null, true);
                const side = sideRef.current;
                customizer.showSideImage = false;
                customizer.appendTo(canvasContainerRef.current);
                customizer.model = model;
                customizer.setDesign(design);
                //TODO add listeners definitions
                (customizer as any).addSelectionChangedListener(handleSelectionChanged);
                (customizer as any).addItemCreatedListener(handleItemCreated);
                (customizer as any).addItemChangedListener(handleItemCreated);
                await customizer.setSide(side);
                customizerRef.current = customizer;
                setIsReady(true);
            }
        })();

        return () => {
            saveDesignChanges();
            if (canvasContainerRef.current) canvasContainerRef.current.innerHTML = '';
        };
    }, []);

    useEffect(() => {
        if (!isReady) return;
        setIsReady(false);
        saveDesignChanges();
        (async () => {
            if (canvasContainerRef.current) canvasContainerRef.current.innerHTML = '';
            const customizer = new MPlaza.Customizer(false, false, null, true);
            sideRef.current = ComposerTemplateNodeAdapter.fromAreaNodeToSide(area);
            const side = sideRef.current;
            if (isReady && model) {
                customizer.showSideImage = false;
                customizer.model = model;
                customizer.appendTo(canvasContainerRef.current!);
                customizer.setDesign(design);
                //TODO add listeners definitions
                (customizer as any).addSelectionChangedListener(handleSelectionChanged);
                (customizer as any).addItemCreatedListener(handleItemCreated);
                (customizer as any).addItemChangedListener(handleItemCreated);
                await customizer.setSide(side);
                customizerRef.current = customizer;
            }
            setIsReady(true);
        })();
    }, [selectedTemplate, selectedArea]);

    const clampPosition = useCallback((x: number, y: number) => {
        const container = containerRef.current;
        const panel = panelRef.current;
        if (!container || !panel) return { x, y };
        const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
        const { width: panelWidth, height: panelHeight } = panel.getBoundingClientRect();
        const maxX = Math.max(16, containerWidth - panelWidth - 16);
        const maxY = Math.max(16, containerHeight - panelHeight - 16);
        return {
            x: Math.min(Math.max(16, x), maxX),
            y: Math.min(Math.max(16, y), maxY)
        };
    }, []);

    const handleModeChange = (mode: EditorMode) => {
        setEditorMode((prev) => {
            if (prev === mode) return null;
            setPanelPosition({ x: 150, y: 20 });
            return mode;
        });
        (customizerRef as any).current?.deselectAll();
        setSelectedDesignItem(null);
        setSelectedMacroCategory(null);
        setSelectedImageCategory(null);
        setActiveTextTab('basic');
    };
    const handleDragStart = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            //checl if clicked point is x close button
            if ((event.target as HTMLElement).closest('button')) return;
            const panel = panelRef.current;
            const container = containerRef.current;
            if (!panel || !container) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            const containerRect = container.getBoundingClientRect();
            dragOffsetRef.current = {
                x: event.clientX - containerRect.left - panelPosition.x,
                y: event.clientY - containerRect.top - panelPosition.y
            };
            setDragPointerId(event.pointerId);
            setIsDragging(true);
        },
        [panelPosition]
    );

    const handleDragMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDragging || dragPointerId !== event.pointerId) return;
            const container = containerRef.current;
            if (!container) return;
            const containerRect = container.getBoundingClientRect();
            const nextX = event.clientX - containerRect.left - dragOffsetRef.current.x;
            const nextY = event.clientY - containerRect.top - dragOffsetRef.current.y;
            setPanelPosition({ x: nextX, y: nextY });
        },
        [clampPosition, dragPointerId, isDragging]
    );

    const handleDragEnd = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (dragPointerId !== event.pointerId) return;
            if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            setDragPointerId(null);
            setIsDragging(false);
        },
        [dragPointerId]
    );

    const toggleRestriction = useCallback(
        (key: keyof RestrictionState) => () => {
            setRestrictions((prev) => ({ ...prev, [key]: !prev[key] }));
        },
        []
    );

    const onImageCategoryChange = useCallback(async (categoryId: string | null) => {
        setSelectedImageCategory(categoryId);
        var images = await fetchQuery<composerQueriesImagesQueriesQuery>(
            Environment,
            composerCategorySelectionQuery,
            {
                categoryID: categoryId,
                count: 50,
                cursor: null,
                isBackground: false,
                isLibrary: false,
                sellerID: null,
                search: ''
            },
            { fetchPolicy: 'network-only' }
        ).toPromise();
        setCurrentImages(
            (images?.images?.edges ?? []).length > 0
                ? images!
                      .images!.edges!.map((i) => i.node)
                      .map((img: any) => ({
                          id: img.imageID,
                          name: img.name,
                          url: img.url
                      }))
                : []
        );
    }, []);

    const closePanel = useCallback(() => setEditorMode(null), []);

    const handleApply = useCallback(() => {
        const customizer = customizerRef.current as any;
        if (selectedDesignItem) {
            selectedDesignItem.set('name', itemInfo.itemName);
            customizer.modifyTextElementContent(selectedPaperItem, itemInfo.text);
            customizer.modifyTextElementFontFamily(selectedPaperItem, itemInfo.fontFamily);
            customizer.modifyTextElementFillColor(selectedPaperItem, itemInfo.color);
            customizer.modifyTextElementJustification(selectedPaperItem, itemInfo.justify);
            if (itemInfo.isPath) {
                customizer.convertToTextOnPath(selectedPaperItem);
            } else {
                customizer.convertToText(selectedPaperItem);
            }
            // Apply text restrictions
            const textConstraints = selectedDesignItem.get('constraints') || new MPlaza.DesignItemConstraints();
            textConstraints.set('canEdit', itemInfo.restrictions.textEditable);
            textConstraints.set('canMove', itemInfo.restrictions.textMoveable);
            textConstraints.set('canRotate', itemInfo.restrictions.textRotable);
            textConstraints.set('canResize', itemInfo.restrictions.textResizable);
            textConstraints.set('canDelete', itemInfo.restrictions.textDeletable);
            textConstraints.set('isAlwaysOnTop', itemInfo.restrictions.isAlwaysOnTop);
            textConstraints.set('isPrintable', itemInfo.restrictions.isPrintable);
            (textConstraints as any).set('keepInPreview', itemInfo.restrictions.printableInPreview);
            textConstraints.set('canChangeFontFamily', itemInfo.restrictions.fontFamilyEditable);
            textConstraints.set('canChangeFontColor', itemInfo.restrictions.textColorEditable);
            textConstraints.set('canChangeFontWeight', itemInfo.restrictions.fontWeightEditable);
            textConstraints.set('canChangeTextPathMode', itemInfo.restrictions.textPathEditable);
            (textConstraints as any).set('toUppercase', itemInfo.restrictions.uppercaseEditable);
            textConstraints.set('minNrChars', itemInfo.restrictions.minCharacters);
            textConstraints.set('maxNrChars', itemInfo.restrictions.maxCharacters);
            textConstraints.set('paddingString', itemInfo.restrictions.paddingString);
            textConstraints.set(
                'paddingTypeID',
                itemInfo.restrictions.padType === 'start'
                    ? MPlaza.PaddingType.Start
                    : itemInfo.restrictions.padType === 'end'
                      ? MPlaza.PaddingType.End
                      : MPlaza.PaddingType.StartEnd
            );
            (selectedDesignItem as any).set('constraints', textConstraints);
        } else {
            customizer.addTextAreaElement(
                { x: 200, y: 200 },
                { text: itemInfo?.text || '', fontFamily: itemInfo?.fontFamily || 'Arial', fontSize: 130, fillColor: itemInfo?.color || '#000000' },
                false
            );
            customizer.convertSelectionToTextArea();
        }
        closePanel();
    }, [closePanel, editorMode, restrictions, itemInfo]);

    const handleImageApply = useCallback(() => {
        if (selectedDesignItem) {
            selectedDesignItem.set('name', imageItemInfo.name);
            // Apply image restrictions
            const imageConstraints = selectedDesignItem.get('constraints') || new MPlaza.DesignItemConstraints();
            imageConstraints.set('canEdit', imageItemInfo.restrictions.imageReplaceable);
            imageConstraints.set('canMove', imageItemInfo.restrictions.imageMoveable);
            imageConstraints.set('canRotate', imageItemInfo.restrictions.imageRotable);
            imageConstraints.set('canResize', imageItemInfo.restrictions.imageResizable);
            imageConstraints.set('canDelete', imageItemInfo.restrictions.imageDeletable);
            imageConstraints.set('isAlwaysOnTop', imageItemInfo.restrictions.alwaysOnTop);
            imageConstraints.set('isPrintable', imageItemInfo.restrictions.isPrintable);
            (imageConstraints as any).set('keepInPreview', imageItemInfo.restrictions.printableInPreview);
            (selectedDesignItem as any).set('constraints', imageConstraints);
        }
        closePanel();
    }, [closePanel, imageItemInfo, selectedDesignItem]);

    const handleImageSelect = useCallback(
        async (image: ComposerCategoriesImages) => {
            const zkImage = new (MPlaza.Image as any)({ imageID: toLocalID(image.id), url: image.url, name: image.name });
            (zkImage as any).set('imageID', image.id);
            zkImage.set('url', image.url);
            zkImage.set('name', image.name);
            console.log('Creating image item for image:', zkImage);
            const customizer = customizerRef.current as any;
            if (customizer) {
                const item = await customizer.insertImage(zkImage, null, { x: 200, y: 200 });
                console.log('Inserted image item:', item);
                customizer.refresh();
            }
            setCurrentImages([]);

            closePanel();
        },
        [closePanel]
    );

    useEffect(() => {
        if (editorMode === null && (dragPointerId !== null || isDragging)) {
            setDragPointerId(null);
            setIsDragging(false);
        }
    }, [editorMode, dragPointerId, isDragging]);

    return (
        <Container>
            {!isReady ? (
                <Loader />
            ) : (
                <>
                    <ComposerDesignToolbar activeMode={editorMode} onModeChange={handleModeChange} />
                    {editorMode && (
                        <FloatingPanel ref={panelRef} style={{ transform: `translate3d(${panelPosition.x}px, ${panelPosition.y}px, 0)` }}>
                            <PanelHeader
                                $dragging={isDragging}
                                onPointerDown={handleDragStart}
                                onPointerMove={handleDragMove}
                                onPointerUp={handleDragEnd}
                                onPointerCancel={handleDragEnd}
                            >
                                <PanelTitle>{editorMode === 'image' ? T._('Image Controls', 'Admin') : T._('Text Controls', 'Admin')}</PanelTitle>
                                <CloseButton type='button' onClick={(e) => closePanel()}>
                                    ✕
                                </CloseButton>
                            </PanelHeader>
                            <PanelBody>
                                {editorMode === 'image' ? (
                                    <>
                                        {imagesMacro && (
                                            <PanelSection>
                                                <FormControl label={T._('Macro Category', 'Admin')}>
                                                    <Select
                                                        options={imagesMacro.imageMacroCategories.map((macro) => ({
                                                            value: macro.id,
                                                            label: macro.name
                                                        }))}
                                                        value={
                                                            selectedMacroCategory
                                                                ? {
                                                                      value: selectedMacroCategory,
                                                                      label:
                                                                          imagesMacro.imageMacroCategories.find((m) => m.id === selectedMacroCategory)?.name ||
                                                                          ''
                                                                  }
                                                                : null
                                                        }
                                                        onChange={(selected) => {
                                                            setSelectedMacroCategory(selected?.value || null);
                                                            setSelectedImageCategory(null);
                                                        }}
                                                        placeholder={T._('Select macro category', 'Admin')}
                                                    />
                                                </FormControl>
                                                <FormControl label={T._('Category', 'Admin')}>
                                                    <Select
                                                        options={
                                                            selectedMacroCategory
                                                                ? imagesMacro.imageMacroCategories
                                                                      .find((macro) => macro.id === selectedMacroCategory)
                                                                      ?.categories.map((cat: any) => ({
                                                                          value: cat.id,
                                                                          label: cat.name
                                                                      })) || []
                                                                : []
                                                        }
                                                        value={
                                                            selectedImageCategory
                                                                ? {
                                                                      value: selectedImageCategory,
                                                                      label:
                                                                          imagesMacro.imageMacroCategories
                                                                              .find((m) => m.id === selectedMacroCategory)
                                                                              ?.categories.find((c) => c.id === selectedImageCategory)?.name || ''
                                                                  }
                                                                : null
                                                        }
                                                        onChange={(selected) => onImageCategoryChange(selected?.value || null)}
                                                        placeholder={T._('Select category', 'Admin')}
                                                        isDisabled={!selectedMacroCategory}
                                                    />
                                                </FormControl>
                                                {currentImages.length > 0 && (
                                                    <FormControl label={T._('Select Image', 'Admin')}>
                                                        <ImageGrid>
                                                            {currentImages.map((image) => (
                                                                <ImageGridItem onClick={() => handleImageSelect(image)}>
                                                                    <img src={image.url} alt={image.name} title={image.name} />
                                                                </ImageGridItem>
                                                            ))}
                                                        </ImageGrid>
                                                    </FormControl>
                                                )}
                                            </PanelSection>
                                        )}
                                        {selectedDesignItem && (
                                            <PanelSection>
                                                <FormControl label={T._('Image Name', 'Admin')}>
                                                    <InlineInput
                                                        value={imageItemInfo.name || ''}
                                                        placeholder='Image name'
                                                        onChange={(event) => setImageItemInfo((prev) => ({ ...prev, name: event.target.value ?? '' }))}
                                                    />
                                                </FormControl>
                                                <FormControl label={T._('Restrictions', 'Admin')}>
                                                    <RestrictionsScrollContainer>
                                                        <RestrictionGroup>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.imageReplaceable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, imageReplaceable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Image Replaceable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.imageMoveable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, imageMoveable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Image Moveable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.imageRotable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, imageRotable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Image Rotable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.imageResizable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, imageResizable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Image Resizable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.imageDeletable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, imageDeletable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Image Deletable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.alwaysOnTop}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, alwaysOnTop: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Always On Top', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.isPrintable}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, isPrintable: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Is Printable', 'Admin')}
                                                            </RestrictionLabel>
                                                            <RestrictionLabel>
                                                                <Checkbox
                                                                    checked={imageItemInfo.restrictions.printableInPreview}
                                                                    onChange={(checked) =>
                                                                        setImageItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, printableInPreview: checked }
                                                                        }))
                                                                    }
                                                                />
                                                                {T._('Printable In Preview', 'Admin')}
                                                            </RestrictionLabel>
                                                        </RestrictionGroup>
                                                    </RestrictionsScrollContainer>
                                                </FormControl>
                                            </PanelSection>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <TabsContainer>
                                            <Tab $active={activeTextTab === 'basic'} onClick={() => setActiveTextTab('basic')}>
                                                {T._('Basic', 'Admin')}
                                            </Tab>
                                            <Tab $active={activeTextTab === 'formatting'} onClick={() => setActiveTextTab('formatting')}>
                                                {T._('Formatting', 'Admin')}
                                            </Tab>
                                            <Tab $active={activeTextTab === 'restrictions'} onClick={() => setActiveTextTab('restrictions')}>
                                                {T._('Restrictions', 'Admin')}
                                            </Tab>
                                        </TabsContainer>

                                        {activeTextTab === 'basic' && (
                                            <TabContent>
                                                <PanelSection>
                                                    <FormControl label={T._('Text content', 'Admin')}>
                                                        <InlineInput
                                                            value={itemInfo?.text || ''}
                                                            placeholder='Type your text'
                                                            onChange={(event) => setItemInfo((prev) => ({ ...prev, text: event.target.value ?? '' }))}
                                                        />
                                                    </FormControl>
                                                    <FormControl label={T._('Item name', 'Admin')}>
                                                        <InlineInput
                                                            value={itemInfo?.itemName || ''}
                                                            placeholder='Item name'
                                                            onChange={(event) => setItemInfo((prev) => ({ ...prev, itemName: event.target.value ?? '' }))}
                                                        />
                                                    </FormControl>
                                                </PanelSection>
                                            </TabContent>
                                        )}

                                        {activeTextTab === 'formatting' && (
                                            <TabContent>
                                                <PanelSection>
                                                    <FormControl label={T._('Font family', 'Admin')}>
                                                        <Select
                                                            options={
                                                                fonts?.fontFamilies?.map((font: any) => ({ value: font.fontFamilyID, label: font.name })) || []
                                                            }
                                                            value={
                                                                fonts?.fontFamilies
                                                                    ?.filter((font: any) => font.name === itemInfo.fontFamily)
                                                                    .map((font: any) => ({ value: font.fontFamilyID, label: font.name }))[0] || null
                                                            }
                                                            onChange={(selected) =>
                                                                setItemInfo((prev) => ({
                                                                    ...prev,
                                                                    fontFamily: selected
                                                                        ? fonts?.fontFamilies?.find((font: any) => font.fontFamilyID === selected.value)
                                                                              ?.name || 'Arial'
                                                                        : 'Arial'
                                                                }))
                                                            }
                                                        />
                                                    </FormControl>
                                                    <FormControl label={T._('Text color', 'Admin')}>
                                                        <ColorPicker
                                                            color={itemInfo.color}
                                                            onChange={(color) =>
                                                                setItemInfo((prev) => ({
                                                                    ...prev,
                                                                    color: color
                                                                }))
                                                            }
                                                        />
                                                    </FormControl>
                                                    <FormControl label={T._('Justification', 'Admin')}>
                                                        <Select
                                                            options={[
                                                                { value: 'left', label: 'Left' },
                                                                { value: 'center', label: 'Center' },
                                                                { value: 'right', label: 'Right' },
                                                                { value: 'justify', label: 'Justify' }
                                                            ]}
                                                            value={{
                                                                value: itemInfo.justify,
                                                                label: itemInfo.justify.charAt(0).toUpperCase() + itemInfo.justify.slice(1)
                                                            }}
                                                            onChange={(selected) =>
                                                                setItemInfo((prev) => ({
                                                                    ...prev,
                                                                    justify: selected ? (selected.value as TextJustify) : 'left'
                                                                }))
                                                            }
                                                        />
                                                    </FormControl>
                                                    <FormControl label={T._('Text on path', 'Admin')}>
                                                        <Checkbox
                                                            checked={itemInfo.isPath}
                                                            onChange={(checked) =>
                                                                setItemInfo((prev) => ({
                                                                    ...prev,
                                                                    isPath: checked
                                                                }))
                                                            }
                                                        />
                                                    </FormControl>
                                                </PanelSection>
                                            </TabContent>
                                        )}

                                        {activeTextTab === 'restrictions' && (
                                            <TabContent>
                                                <PanelSection>
                                                    <FormControl label={T._('Restrictions', 'Admin')}>
                                                        <RestrictionsScrollContainer>
                                                            <RestrictionGroup>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textMoveable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textMoveable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Moveable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textRotable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textRotable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Rotable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textResizable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textResizable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Resizable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textDeletable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textDeletable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Deletable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.isAlwaysOnTop}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, isAlwaysOnTop: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Always On Top', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.isPrintable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, isPrintable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Is Printable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.printableInPreview}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, printableInPreview: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Printable In Preview', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.fontFamilyEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, fontFamilyEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Font Family Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textColorEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textColorEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Color Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.fontWeightEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, fontWeightEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Font Weight Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.textPathEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, textPathEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Text Path Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                                <RestrictionLabel>
                                                                    <Checkbox
                                                                        checked={itemInfo.restrictions.uppercaseEditable}
                                                                        onChange={(checked) =>
                                                                            setItemInfo((prev) => ({
                                                                                ...prev,
                                                                                restrictions: { ...prev.restrictions, uppercaseEditable: checked }
                                                                            }))
                                                                        }
                                                                    />
                                                                    {T._('Uppercase Editable', 'Admin')}
                                                                </RestrictionLabel>
                                                            </RestrictionGroup>
                                                            <FormControl label={T._('Min Characters', 'Admin')}>
                                                                <InlineInput
                                                                    type='number'
                                                                    min='0'
                                                                    value={itemInfo.restrictions.minCharacters}
                                                                    onChange={(event) =>
                                                                        setItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: {
                                                                                ...prev.restrictions,
                                                                                minCharacters: parseInt(event.target.value) || 0
                                                                            }
                                                                        }))
                                                                    }
                                                                />
                                                            </FormControl>
                                                            <FormControl label={T._('Max Characters', 'Admin')}>
                                                                <InlineInput
                                                                    type='number'
                                                                    min='0'
                                                                    value={itemInfo.restrictions.maxCharacters}
                                                                    onChange={(event) =>
                                                                        setItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: {
                                                                                ...prev.restrictions,
                                                                                maxCharacters: parseInt(event.target.value) || 0
                                                                            }
                                                                        }))
                                                                    }
                                                                />
                                                            </FormControl>
                                                            <FormControl label={T._('Padding String', 'Admin')}>
                                                                <InlineInput
                                                                    value={itemInfo.restrictions.paddingString}
                                                                    onChange={(event) =>
                                                                        setItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: { ...prev.restrictions, paddingString: event.target.value }
                                                                        }))
                                                                    }
                                                                />
                                                            </FormControl>
                                                            <FormControl label={T._('Pad Type', 'Admin')}>
                                                                <Select
                                                                    options={[
                                                                        { value: 'start', label: 'Start' },
                                                                        { value: 'end', label: 'End' },
                                                                        { value: 'both', label: 'Both' }
                                                                    ]}
                                                                    value={{
                                                                        value: itemInfo.restrictions.padType,
                                                                        label:
                                                                            itemInfo.restrictions.padType.charAt(0).toUpperCase() +
                                                                            itemInfo.restrictions.padType.slice(1)
                                                                    }}
                                                                    onChange={(selected) =>
                                                                        setItemInfo((prev) => ({
                                                                            ...prev,
                                                                            restrictions: {
                                                                                ...prev.restrictions,
                                                                                padType: selected ? (selected.value as 'start' | 'end' | 'both') : 'start'
                                                                            }
                                                                        }))
                                                                    }
                                                                />
                                                            </FormControl>
                                                        </RestrictionsScrollContainer>
                                                    </FormControl>
                                                </PanelSection>
                                            </TabContent>
                                        )}
                                    </>
                                )}
                                <InlineActions>
                                    <Button type='button' onClick={editorMode === 'image' ? handleImageApply : handleApply}>
                                        {selectedDesignItem ? T._('Save changes', 'Admin') : T._('Create', 'Admin')}
                                    </Button>
                                    <Button type='button' onClick={closePanel}>
                                        {T._('Cancel', 'Admin')}
                                    </Button>
                                </InlineActions>
                            </PanelBody>
                        </FloatingPanel>
                    )}
                </>
            )}
            <CanvasContainer ref={canvasContainerRef} />
        </Container>
    );
};
